/**
 * SVG do pino do mapa — GUPM-247. TS puro (sem React/Leaflet/DOM): o `InstrumentMap`
 * só embrulha o resultado num `L.divIcon`.
 *
 * Extraído do componente para que a distinção **por forma** entre estado com lastro
 * e sem lastro fosse testável. Ela era, antes, uma alegação: `MapPin.outline` existia,
 * ninguém o lia, e o comentário do token afirmava conformidade com WCAG 1.4.1 que o
 * código não entregava — todo pino saía sólido, e "sem faixa" se distinguia de
 * "sem leitura" apenas por um tom de cinza.
 */
import type { InstrumentType } from '@/domain';
import type { MapPin } from './pins';

/**
 * Forma por tipo de instrumento (viewBox 28×28). A forma É o pino — preenchida com
 * a cor do nível, sem círculo de fundo.
 */
export const TYPE_SHAPE: Record<InstrumentType, string> = {
  PZ: '<circle cx="14" cy="14" r="9" />',
  INA: '<polygon points="14,3 25,24 3,24" />',
  MS: '<rect x="5" y="5" width="18" height="18" rx="2" />',
  MSE: '<polygon points="14,2 26,14 14,26 2,14" />',
  TILT: '<polygon points="14,1 17.6,10 27,10.3 19.4,16 22.2,25 14,19.6 5.8,25 8.6,16 1,10.3 10.4,10" />',
  PLV: '<polygon points="14,2 26,14 14,26 2,14" />',
  MV: '<polygon points="14,2 26,14 14,26 2,14" />',
  RL: '<polygon points="14,2 26,14 14,26 2,14" />',
  // GUPM-127 — v2 corporativa (sem coord neste card → não renderiza no mapa).
  EM: '<polygon points="14,2 26,14 14,26 2,14" />',
  INC: '<polygon points="14,2 26,14 14,26 2,14" />',
};

/** Lado do ícone em px. Selecionado cresce para destacar sem mudar de forma. */
export function tamanhoDoPino(selected: boolean): number {
  return selected ? 34 : 26;
}

/**
 * SVG do pino.
 *
 * - **Com lastro** → forma preenchida com a cor do nível, contorno escuro fino só
 *   para legibilidade sobre as tiles.
 * - **Sem lastro** (`pin.outline` — hoje `SEM_FAIXA` e, desde o GUPM-303, `SEM_SINAL`;
 *   a lista vive em `buildMapPins`) → **vazado**: `fill="none"`
 *   e traço tracejado na cor do estado. Diferença de forma, não só de matiz — é o
 *   que WCAG 1.4.1 exige e o dispositivo `fill × outline` do manual da marca.
 *
 * Selecionado mantém o amarelo da marca no traço nos dois casos (é foco, não estado),
 * mas o vazado continua vazado — senão selecionar um instrumento sem faixa o faria
 * parecer medido.
 */
export function svgDoPino(pin: MapPin, selected: boolean): string {
  const size = tamanhoDoPino(selected);
  const shadow = selected
    ? 'drop-shadow(0 0 4px rgba(254,203,0,.9))'
    : 'drop-shadow(0 1px 1px rgba(0,0,0,.5))';

  const fill = pin.outline ? 'none' : pin.color;
  // Vazado: o traço carrega a cor (é ele que informa o estado). Sólido: traço só
  // separa a forma do fundo.
  const stroke = selected ? '#FECB00' : pin.outline ? pin.color : 'rgba(15,23,41,.85)';
  const strokeWidth = selected ? 2.5 : pin.outline ? 2 : 1.5;
  const dash = pin.outline ? ' stroke-dasharray="3 2"' : '';

  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 28 28" style="overflow:visible;filter:${shadow}">`
    + `<g fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"${dash} stroke-linejoin="round">`
    + `${TYPE_SHAPE[pin.type]}</g></svg>`
  );
}
